import React, { useMemo } from 'react';
import Card from '@/components/UI/Card';
import DataTable from '@/components/UI/DataTable';
import EmptyState from '@/components/UI/EmptyState';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { PortfolioStrategySubscriptionChangesRow, TradingStrategyForPortfolio } from '@einhaus/project314-types';

interface PortfolioSubscriptionChangesCardProps {
    portfolioId: string | undefined;
    /** Current subscriptions name the strategies; removed ones fall back to their id. */
    strategiesSubscribed: TradingStrategyForPortfolio[] | null | undefined;
}

type SubscriptionChangeType = PortfolioStrategySubscriptionChangesRow['changeType'];
type SubscriptionChangeSource = PortfolioStrategySubscriptionChangesRow['source'];

const CHANGE_BADGE_CLASS: Record<SubscriptionChangeType, string> = {
    subscribed: 'bg-blue-900 text-blue-200',
    unsubscribed: 'bg-zinc-800 text-zinc-300',
    activated: 'bg-green-900 text-green-200',
    deactivated: 'bg-red-900 text-red-200',
    deploymentUpdated: 'bg-amber-900 text-amber-200'
};

const CHANGE_LABEL: Record<SubscriptionChangeType, string> = {
    subscribed: 'Added',
    unsubscribed: 'Removed',
    activated: 'Turned on',
    deactivated: 'Turned off',
    deploymentUpdated: 'Settings changed'
};

const SOURCE_LABEL: Record<SubscriptionChangeSource, string> = {
    web: 'Web',
    agentTool: 'Assistant',
    script: 'Script'
};

const describeOnOff = (value: number | null): string => (value === null ? '—' : value ? 'On' : 'Off');

const describeProfile = (value: number | null): string => (value === null ? '—' : `#${value}`);

const PortfolioSubscriptionChangesCard: React.FC<PortfolioSubscriptionChangesCardProps> = ({ portfolioId, strategiesSubscribed }) => {
    const numericPortfolioId = portfolioId ? parseInt(portfolioId) : 0;

    const strategyNameById = useMemo(
        () => new Map((strategiesSubscribed ?? []).map((strategy) => [strategy.id, strategy.name])),
        [strategiesSubscribed]
    );

    const changesQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioStrategySubscriptionChanges.queryOptions({ portfolioId: numericPortfolioId, limit: 200 }),
            enabled: numericPortfolioId > 0
        },
        { fallbackErrorMessage: 'Failed to load strategy change history' }
    );

    const changes = changesQuery.data?.changes ?? [];

    const columns = useMemo<ColumnDef<PortfolioStrategySubscriptionChangesRow>[]>(
        () => [
            {
                accessorKey: 'createdAt',
                header: 'When',
                size: 170
            },
            {
                id: 'strategy',
                header: 'Strategy',
                accessorFn: (row) => strategyNameById.get(row.strategyId) ?? `Strategy #${row.strategyId}`,
                size: 220
            },
            {
                accessorKey: 'changeType',
                header: 'Change',
                cell: ({ row }) => {
                    const { changeType } = row.original;

                    return (
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${CHANGE_BADGE_CLASS[changeType]}`}>
                            {CHANGE_LABEL[changeType]}
                        </span>
                    );
                },
                size: 130
            },
            {
                id: 'onOff',
                header: 'On / off',
                accessorFn: (row) => `${describeOnOff(row.previousIsActive)} → ${describeOnOff(row.nextIsActive)}`,
                size: 110
            },
            {
                id: 'tradingProfile',
                header: 'Profile',
                accessorFn: (row) => `${describeProfile(row.previousTradingProfileId)} → ${describeProfile(row.nextTradingProfileId)}`,
                size: 120
            },
            {
                id: 'who',
                header: 'Who',
                accessorFn: (row) =>
                    [SOURCE_LABEL[row.source], row.sourceDetail, row.actorUserId === null ? null : `user #${row.actorUserId}`]
                        .filter((part) => part !== null)
                        .join(' · '),
                size: 200
            },
            {
                id: 'device',
                header: 'Device',
                accessorFn: (row) => [row.ipAddress, row.userAgent].filter((part) => part !== null).join(' · ') || '—',
                size: 260
            }
        ],
        [strategyNameById]
    );

    return (
        <Card
            className="mb-4"
            subtitle="Every strategy added, removed, turned on or off, or reconfigured on this portfolio, and who did it."
            title="Strategy Change History"
        >
            {changes.length === 0 ? (
                <EmptyState size="sm" title="No strategy changes recorded yet." />
            ) : (
                <DataTable search striped columns={columns} data={changes} pagination={{ enabled: true, pageSize: 25 }} />
            )}
        </Card>
    );
};

export default PortfolioSubscriptionChangesCard;
